import React, { useState, useEffect } from 'react';
import { apiGet, apiPost, apiPut, apiDelete } from '../utils/api';

interface Tournament {
  id: string;
  name: string;
  format?: string;
}

interface TournamentLevel {
  level: string;
  playerCount: number;
}

interface Player {
  id: string;
  name: string;
}

interface CourtAvailability {
  id: string;
  date: string;
  startTime: string;
  endTime: string;
  court: {
    id: string;
    name: string;
    club: {
      id: string;
      name: string;
    };
  };
}

interface MatchProposal {
  id: string;
  tournamentId: string;
  groupId?: string;
  player1Id: string;
  player2Id: string;
  player3Id?: string;
  player4Id?: string;
  player1: Player;
  player2: Player;
  player3?: Player;
  player4?: Player;
  availabilityId: string;
  availability: CourtAvailability;
  status: string; // PENDING, SENT, ACCEPTED, REJECTED, EXPIRED, MATCH_CREATED
  responses: string; // JSON массив ответов
  sentAt?: string;
  respondedAt?: string;
  createdAt: string;
  tournament: {
    id: string;
    name: string;
  };
  group?: {
    id: string;
    name: string;
    level: string;
  };
}

interface MatchSuggestion {
  player1: Player;
  player2: Player;
  player3?: Player;
  player4?: Player;
  availableSlots: Array<{
    id: string;
    date: Date;
    startTime: string;
    endTime: string;
    court: {
      id: string;
      name: string;
      club: {
        id: string;
        name: string;
      };
    };
  }>;
  priority: number;
}

interface TournamentGroup {
  id: string;
  name: string;
  level: string;
}

const MatchProposals: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [levels, setLevels] = useState<TournamentLevel[]>([]);
  const [selectedLevel, setSelectedLevel] = useState<string>('');
  const [groups, setGroups] = useState<TournamentGroup[]>([]);
  const [selectedGroup, setSelectedGroup] = useState<string>('');
  const [proposals, setProposals] = useState<MatchProposal[]>([]);
  const [players, setPlayers] = useState<Player[]>([]);
  const [availableSlots, setAvailableSlots] = useState<CourtAvailability[]>([]);
  const [suggestions, setSuggestions] = useState<MatchSuggestion[]>([]);
  const [loading, setLoading] = useState(false);
  
  // Для публичных слотов
  const [selectedSlot, setSelectedSlot] = useState<string>('');
  const [slotConfirmedPlayers, setSlotConfirmedPlayers] = useState<any[]>([]);
  const [showSlotPlayers, setShowSlotPlayers] = useState(false);
  
  // Модальное окно для создания/редактирования предложения
  const [showModal, setShowModal] = useState(false);
  const [editingProposal, setEditingProposal] = useState<MatchProposal | null>(null);
  const [selectedPlayer1, setSelectedPlayer1] = useState<string>('');
  const [selectedPlayer2, setSelectedPlayer2] = useState<string>('');
  const [selectedPlayer3, setSelectedPlayer3] = useState<string>('');
  const [selectedPlayer4, setSelectedPlayer4] = useState<string>('');
  const [selectedAvailability, setSelectedAvailability] = useState<string>('');
  
  // Модальное окно для ввода ответа игрока
  const [showResponseModal, setShowResponseModal] = useState(false);
  const [responseProposal, setResponseProposal] = useState<MatchProposal | null>(null);
  const [responsePlayerId, setResponsePlayerId] = useState<string>('');
  const [responseStatus, setResponseStatus] = useState<'ACCEPTED' | 'REJECTED'>('ACCEPTED');

  useEffect(() => {
    fetchTournaments();
  }, []);

  useEffect(() => {
    if (selectedTournament) {
      fetchLevels();
    }
  }, [selectedTournament]);

  useEffect(() => {
    if (selectedTournament) {
      fetchAvailableSlots(); // Загружаем слоты при выборе турнира
      if (selectedLevel) {
        fetchGroups();
      }
      fetchProposals();
      if (selectedGroup) {
        fetchSuggestions();
      }
    }
  }, [selectedTournament, selectedLevel, selectedGroup]);

  const fetchTournaments = async () => {
    try {
      const data = await apiGet('/tournaments?limit=1000');
      setTournaments(data.tournaments || []);
    } catch (error) {
      console.error('Ошибка загрузки турниров:', error);
    }
  };

  const fetchLevels = async () => {
    if (!selectedTournament) return;
    try {
      const data = await apiGet(`/tournaments/${selectedTournament.id}`);
      // Получаем уровни из групп
      const groupsData = await apiGet(`/tournament-groups/tournament/${selectedTournament.id}`);
      const levelMap = new Map<string, number>();
      groupsData.forEach((group: any) => {
        const count = levelMap.get(group.level) || 0;
        levelMap.set(group.level, count + 1);
      });
      const levelsList: TournamentLevel[] = Array.from(levelMap.entries()).map(([level, count]) => ({
        level,
        playerCount: count
      }));
      setLevels(levelsList);
    } catch (error) {
      console.error('Ошибка загрузки уровней:', error);
    }
  };

  const fetchGroups = async () => {
    if (!selectedTournament || !selectedLevel) return;
    try {
      const data = await apiGet(`/tournament-groups/tournament/${selectedTournament.id}/level/${selectedLevel}`);
      setGroups(data || []);
      if (data && data.length > 0 && !selectedGroup) {
        setSelectedGroup(data[0].id);
      }
    } catch (error) {
      console.error('Ошибка загрузки групп:', error);
    }
  };

  const fetchProposals = async () => {
    if (!selectedTournament) return;
    
    // Если не выбран уровень - загружаем все предложения турнира
    if (!selectedLevel) {
      try {
        // Получаем все предложения турнира
        // Для этого нужно получить все группы турнира и загрузить предложения для каждой
        const tournamentData = await apiGet(`/tournaments/${selectedTournament.id}`);
        const allGroups = await apiGet(`/tournament-groups/tournament/${selectedTournament.id}`);
        const allProposals: MatchProposal[] = [];
        
        for (const group of allGroups || []) {
          try {
            const data = await apiGet(`/match-proposals/tournament/${selectedTournament.id}/group/${group.id}`);
            if (data && Array.isArray(data)) {
              allProposals.push(...data);
            }
          } catch (error) {
            console.error(`Ошибка загрузки предложений для группы ${group.id}:`, error);
          }
        }
        
        setProposals(allProposals);
        return;
      } catch (error) {
        console.error('Ошибка загрузки предложений:', error);
        return;
      }
    }
    
    try {
      if (selectedGroup) {
        // Если выбрана группа - загружаем предложения только этой группы
        const data = await apiGet(`/match-proposals/tournament/${selectedTournament.id}/group/${selectedGroup}`);
        setProposals(data || []);
      } else {
        // Если группа не выбрана - загружаем предложения всех групп уровня
        const data = await apiGet(`/match-proposals/tournament/${selectedTournament.id}/level/${selectedLevel}`);
        setProposals(data || []);
      }
    } catch (error) {
      console.error('Ошибка загрузки предложений:', error);
    }
  };

  const fetchSuggestions = async () => {
    if (!selectedTournament || !selectedGroup) return;
    try {
      const data = await apiGet(`/match-suggestions/${selectedTournament.id}/${selectedGroup}`);
      setSuggestions(data || []);
    } catch (error) {
      console.error('Ошибка загрузки предложений системы:', error);
      setSuggestions([]);
    }
  };

  const fetchAvailableSlots = async () => {
    if (!selectedTournament) return;
    try {
      // Получаем доступные слоты для клубов турнира
      const tournamentData = await apiGet(`/tournaments/${selectedTournament.id}`);
      const clubIds = tournamentData.tournamentClubs?.map((tc: any) => tc.clubId) || [];
      
      if (clubIds.length === 0) {
        setAvailableSlots([]);
        return;
      }
      
      // Получаем все доступные слоты
      const allSlots = await apiGet('/court-availability');
      const filteredSlots = allSlots.filter((slot: any) => 
        clubIds.includes(slot.clubId) && 
        slot.isAvailable &&
        new Date(slot.date) >= new Date() // Только будущие даты
      );
      setAvailableSlots(filteredSlots);
    } catch (error) {
      console.error('Ошибка загрузки доступных слотов:', error);
    }
  };

  const fetchPlayers = async () => {
    if (!selectedTournament) return;
    
    try {
      if (selectedGroup) {
        // Если выбрана группа - загружаем игроков группы
        const groupData = await apiGet(`/tournament-groups/${selectedGroup}`);
        const playersData = groupData.memberships?.map((m: any) => m.player) || [];
        setPlayers(playersData);
      } else if (selectedLevel) {
        // Если не выбрана группа, но выбран уровень - загружаем всех игроков уровня из всех групп
        const allGroups = await apiGet(`/tournament-groups/tournament/${selectedTournament.id}/level/${selectedLevel}`);
        const allPlayersMap = new Map<string, Player>();
        
        for (const group of allGroups || []) {
          const groupData = await apiGet(`/tournament-groups/${group.id}`);
          const playersData = groupData.memberships?.map((m: any) => m.player) || [];
          playersData.forEach((player: Player) => {
            if (!allPlayersMap.has(player.id)) {
              allPlayersMap.set(player.id, player);
            }
          });
        }
        
        setPlayers(Array.from(allPlayersMap.values()));
      } else {
        // Если не выбран ни уровень, ни группа - загружаем всех игроков турнира
        const allGroups = await apiGet(`/tournament-groups/tournament/${selectedTournament.id}`);
        const allPlayersMap = new Map<string, Player>();
        
        for (const group of allGroups || []) {
          const groupData = await apiGet(`/tournament-groups/${group.id}`);
          const playersData = groupData.memberships?.map((m: any) => m.player) || [];
          playersData.forEach((player: Player) => {
            if (!allPlayersMap.has(player.id)) {
              allPlayersMap.set(player.id, player);
            }
          });
        }
        
        setPlayers(Array.from(allPlayersMap.values()));
      }
    } catch (error) {
      console.error('Ошибка загрузки игроков:', error);
    }
  };

  const handleCreateFromSuggestion = async (suggestion: MatchSuggestion, slotId: string) => {
    if (!selectedTournament || !selectedGroup) return;
    
    try {
      await apiPost('/match-proposals', {
        tournamentId: selectedTournament.id,
        groupId: selectedGroup,
        player1Id: suggestion.player1.id,
        player2Id: suggestion.player2.id,
        player3Id: suggestion.player3?.id,
        player4Id: suggestion.player4?.id,
        availabilityId: slotId,
        status: 'PENDING'
      });
      
      await fetchProposals();
      alert('Предложение создано');
    } catch (error: any) {
      console.error('Ошибка создания предложения:', error);
      alert('Ошибка создания предложения: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleCreateProposal = async () => {
    if (!selectedTournament || !selectedPlayer1 || !selectedPlayer2 || !selectedAvailability) {
      alert('Заполните все обязательные поля (турнир, игроки, слот)');
      return;
    }

    // Определяем режим создания:
    // 1. Если выбрана группа - создаем для этой группы
    // 2. Если не выбрана группа, но выбран уровень - создаем для всех групп уровня
    // 3. Если не выбраны ни группа, ни уровень - создаем для всех групп всех уровней турнира
    const createForAllGroups = !selectedGroup && selectedLevel;
    const createForAllLevels = !selectedGroup && !selectedLevel;

    try {
      const response = await apiPost('/match-proposals', {
        tournamentId: selectedTournament.id,
        groupId: selectedGroup || undefined,
        level: createForAllGroups ? selectedLevel : undefined,
        // Если не указаны ни level, ни groupId - создаем для всего турнира
        player1Id: selectedPlayer1,
        player2Id: selectedPlayer2,
        player3Id: selectedPlayer3 || undefined,
        player4Id: selectedPlayer4 || undefined,
        availabilityId: selectedAvailability,
        status: 'PENDING'
      });
      
      if (createForAllLevels && response.proposals) {
        await fetchProposals();
        alert(`Создано ${response.proposals.length} предложений для всех уровней и групп турнира`);
      } else if (createForAllGroups && response.proposals) {
        await fetchProposals();
        alert(`Создано ${response.proposals.length} предложений для всех групп уровня ${selectedLevel}`);
      } else {
        await fetchProposals();
        alert('Предложение создано');
      }
      
      setShowModal(false);
      resetModal();
    } catch (error: any) {
      console.error('Ошибка создания предложения:', error);
      alert('Ошибка создания предложения: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleAddResponse = async () => {
    if (!responseProposal || !responsePlayerId) return;

    try {
      await apiPost(`/match-proposals/${responseProposal.id}/response`, {
        playerId: responsePlayerId,
        response: responseStatus
      });
      
      await fetchProposals();
      setShowResponseModal(false);
      setResponseProposal(null);
      alert('Ответ добавлен');
    } catch (error: any) {
      console.error('Ошибка добавления ответа:', error);
      alert('Ошибка добавления ответа: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleDeleteProposal = async (id: string) => {
    if (!confirm('Удалить предложение?')) return;

    try {
      await apiDelete(`/match-proposals/${id}`);
      await fetchProposals();
      alert('Предложение удалено');
    } catch (error: any) {
      console.error('Ошибка удаления предложения:', error);
      alert('Ошибка удаления предложения: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleSendAllProposals = async () => {
    if (!selectedTournament) {
      alert('Выберите турнир');
      return;
    }

    // Сначала получаем количество предложений для отправки
    try {
      const countResponse = await apiPost('/match-proposals/send/count', {
        tournamentId: selectedTournament.id,
        groupId: selectedGroup || undefined,
        level: (!selectedGroup && selectedLevel) ? selectedLevel : undefined
      });

      if (countResponse.count === 0) {
        alert('Нет предложений для отправки (все уже отправлены или имеют финальный статус)');
        return;
      }

      // Формируем детальное сообщение
      let confirmMessage = '';
      let detailsMessage = '';

      if (selectedGroup) {
        const groupName = groups.find(g => g.id === selectedGroup)?.name || 'группы';
        confirmMessage = `Отправить все предложения группы "${groupName}"?`;
        detailsMessage = `\n\nБудет отправлено: ${countResponse.count} предложений`;
      } else if (selectedLevel) {
        confirmMessage = `Отправить все предложения уровня ${selectedLevel}?`;
        if (countResponse.details?.groups) {
          const groupsList = countResponse.details.groups
            .map((g: any) => `  • ${g.groupName}: ${g.count} предложений`)
            .join('\n');
          detailsMessage = `\n\nБудет отправлено: ${countResponse.count} предложений\n\nПо группам:\n${groupsList}`;
        } else {
          detailsMessage = `\n\nБудет отправлено: ${countResponse.count} предложений`;
        }
      } else {
        confirmMessage = `Отправить все предложения турнира "${selectedTournament.name}"?`;
        if (countResponse.details?.levels) {
          const levelsList = countResponse.details.levels
            .map((l: any) => `  • Уровень ${l.level}: ${l.count} предложений`)
            .join('\n');
          detailsMessage = `\n\nБудет отправлено: ${countResponse.count} предложений\n\nПо уровням:\n${levelsList}`;
        } else {
          detailsMessage = `\n\nБудет отправлено: ${countResponse.count} предложений`;
        }
      }

      if (!confirm(confirmMessage + detailsMessage)) return;

      // Отправляем предложения
      const response = await apiPost('/match-proposals/send', {
        tournamentId: selectedTournament.id,
        groupId: selectedGroup || undefined,
        level: (!selectedGroup && selectedLevel) ? selectedLevel : undefined
      });
      
      await fetchProposals();
      alert(`Отправлено ${response.count} предложений`);
    } catch (error: any) {
      console.error('Ошибка отправки предложений:', error);
      alert('Ошибка отправки предложений: ' + (error.response?.data?.error || error.message));
    }
  };

  const handleOpenModal = () => {
    if (!selectedTournament) {
      alert('Выберите турнир');
      return;
    }
    fetchAvailableSlots();
    fetchPlayers();
    setShowModal(true);
  };

  const handleOpenResponseModal = (proposal: MatchProposal) => {
    setResponseProposal(proposal);
    setResponsePlayerId('');
    setResponseStatus('ACCEPTED');
    setShowResponseModal(true);
  };

  const resetModal = () => {
    setSelectedPlayer1('');
    setSelectedPlayer2('');
    setSelectedPlayer3('');
    setSelectedPlayer4('');
    setSelectedAvailability('');
    setEditingProposal(null);
  };

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      PENDING: 'Ожидает',
      SENT: 'Отправлено',
      ACCEPTED: 'Принято',
      REJECTED: 'Отклонено',
      EXPIRED: 'Истекло',
      MATCH_CREATED: 'Матч создан'
    };
    return labels[status] || status;
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      PENDING: 'bg-gray-100 text-gray-800',
      SENT: 'bg-blue-100 text-blue-800',
      ACCEPTED: 'bg-green-100 text-green-800',
      REJECTED: 'bg-red-100 text-red-800',
      EXPIRED: 'bg-yellow-100 text-yellow-800',
      MATCH_CREATED: 'bg-purple-100 text-purple-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const parseResponses = (responsesJson: string) => {
    try {
      return JSON.parse(responsesJson || '[]');
    } catch {
      return [];
    }
  };

  const getPlayerResponse = (proposal: MatchProposal, playerId: string) => {
    const responses = parseResponses(proposal.responses);
    return responses.find((r: any) => r.playerId === playerId);
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('ru-RU');
  };

  const formatTime = (timeStr: string) => {
    return timeStr;
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Предложения игрокам</h1>

      {/* Фильтры */}
      <div className="bg-white p-4 rounded-lg shadow-md mb-6">
        <div className="grid grid-cols-3 gap-4 mb-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Турнир
            </label>
            <select
              value={selectedTournament?.id || ''}
              onChange={(e) => {
                const tournament = tournaments.find(t => t.id === e.target.value);
                setSelectedTournament(tournament || null);
                setSelectedLevel('');
                setSelectedGroup('');
                setProposals([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
            >
              <option value="">Выберите турнир</option>
              {tournaments.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Уровень (необязательно)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              Если не выбран, создастся для всех уровней турнира
            </p>
            <select
              value={selectedLevel}
              onChange={(e) => {
                setSelectedLevel(e.target.value);
                setSelectedGroup('');
                setProposals([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={!selectedTournament}
            >
              <option value="">Выберите уровень (или оставьте для всех уровней)</option>
              {levels.map(l => (
                <option key={l.level} value={l.level}>
                  {l.level} ({l.playerCount} игроков)
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Группа (необязательно)
            </label>
            <p className="text-xs text-gray-500 mb-2">
              {selectedLevel 
                ? 'Если не выбрана, создастся для всех групп уровня'
                : !selectedLevel
                ? 'Если не выбраны уровень и группа, создастся для всех уровней и групп турнира'
                : ''
              }
            </p>
            <select
              value={selectedGroup}
              onChange={(e) => {
                setSelectedGroup(e.target.value);
                setProposals([]);
              }}
              className="w-full border border-gray-300 rounded-lg px-3 py-2"
              disabled={!selectedLevel}
            >
              <option value="">Все группы уровня {selectedLevel || ''}</option>
              {groups.filter(g => g).map(g => (
                <option key={g.id} value={g.id}>
                  {g.name} ({g.level})
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Секция публичных слотов */}
        {selectedTournament && (
          <div className="bg-white p-4 rounded-lg shadow-md mb-6">
            <h2 className="text-lg font-semibold mb-4">Публичные слоты - Подтверждения игроков</h2>
            <div className="grid grid-cols-2 gap-4 mb-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Выберите слот (корт X в клубе Y, время ЧЧ:ММ +2ч)
                </label>
                <select
                  value={selectedSlot}
                  onChange={(e) => {
                    setSelectedSlot(e.target.value);
                    setSlotConfirmedPlayers([]);
                    setShowSlotPlayers(false);
                  }}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Выберите слот</option>
                  {availableSlots.filter(slot => slot && slot.court && slot.court.club).map(slot => (
                    <option key={slot.id} value={slot.id}>
                      {formatDate(slot.date)} {formatTime(slot.startTime)} - {slot.court.name} ({slot.court.club.name})
                    </option>
                  ))}
                </select>
              </div>
              <div className="flex items-end">
                <button
                  onClick={async () => {
                    if (!selectedSlot) {
                      alert('Выберите слот');
                      return;
                    }
                    try {
                      const data = await apiGet(`/player-availability/availability/${selectedSlot}`);
                      setSlotConfirmedPlayers(data || []);
                      setShowSlotPlayers(true);
                    } catch (error) {
                      console.error('Ошибка загрузки подтверждений:', error);
                      alert('Ошибка загрузки подтверждений');
                    }
                  }}
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                  disabled={!selectedSlot}
                >
                  Показать подтверждения
                </button>
              </div>
            </div>

            {/* Список подтвердивших игроков */}
            {showSlotPlayers && slotConfirmedPlayers.length > 0 && (
              <div className="mt-4">
                <h3 className="text-md font-medium mb-2">
                  Подтвердили слот ({slotConfirmedPlayers.length} игроков):
                </h3>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Игрок</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Турнир</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Подтвердил</th>
                        <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {slotConfirmedPlayers.map((pa: any) => (
                        <tr key={pa.id}>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {pa.player?.name || 'Неизвестный игрок'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            {pa.tournament?.name || '—'}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500">
                            {new Date(pa.createdAt).toLocaleString('ru-RU')}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap text-sm">
                            <button
                              onClick={() => {
                                setSelectedPlayer1(pa.player.id);
                                setSelectedAvailability(selectedSlot);
                                handleOpenModal();
                              }}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              Создать предложение
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div className="mt-4 flex gap-2">
                  <button
                    onClick={() => {
                      if (slotConfirmedPlayers.length < 2) {
                        alert('Нужно минимум 2 игрока для создания предложения');
                        return;
                      }
                      // Открываем модальное окно с предзаполненными данными
                      setSelectedAvailability(selectedSlot);
                      handleOpenModal();
                    }}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                  >
                    Создать предложение из подтвердивших
                  </button>
                </div>
              </div>
            )}
            {showSlotPlayers && slotConfirmedPlayers.length === 0 && (
              <div className="mt-4 text-center py-4 text-gray-500">
                Пока никто не подтвердил этот слот
              </div>
            )}
          </div>
        )}

        {selectedTournament && (
          <div className="flex gap-2">
            <button
              onClick={handleOpenModal}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
            >
              + Создать предложение
              {selectedLevel && !selectedGroup 
                ? ` для всех групп уровня ${selectedLevel}`
                : !selectedLevel && !selectedGroup
                ? ` для всех уровней и групп турнира`
                : ''
              }
            </button>
            <button
              onClick={handleSendAllProposals}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
              title={selectedGroup 
                ? `Отправить все предложения группы`
                : selectedLevel
                ? `Отправить все предложения уровня ${selectedLevel}`
                : `Отправить все предложения турнира "${selectedTournament.name}" (все уровни и группы)`
              }
              disabled={false}
            >
              📤 Отправить все{selectedLevel && !selectedGroup ? ` (уровень ${selectedLevel})` : selectedGroup ? ` (группа)` : ` (турнир)`}
            </button>
          </div>
        )}
      </div>

      {/* Предложения системы */}
      {suggestions.length > 0 && (
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <h2 className="text-lg font-semibold mb-4">Предложения системы (по приоритету)</h2>
          <div className="space-y-2 max-h-64 overflow-y-auto">
            {suggestions.slice(0, 5).map((suggestion, idx) => (
              <div key={idx} className="border border-gray-200 rounded p-3">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="font-medium">
                      {suggestion.player1?.name || 'Игрок 1'} vs {suggestion.player2?.name || 'Игрок 2'}
                      {suggestion.player3 && ` / ${suggestion.player3?.name || 'Игрок 3'} vs ${suggestion.player4?.name || 'Игрок 4'}`}
                    </span>
                    <span className="ml-2 text-sm text-gray-500">
                      (Приоритет: {suggestion.priority})
                    </span>
                  </div>
                  <div className="flex gap-2">
                    {suggestion.availableSlots.slice(0, 3).map((slot) => (
                      <button
                        key={slot.id}
                        onClick={() => handleCreateFromSuggestion(suggestion, slot.id)}
                        className="px-3 py-1 bg-green-600 text-white text-sm rounded hover:bg-green-700"
                        title={`${slot.date ? formatDate(slot.date.toString()) : ''} ${slot.startTime || ''} - ${slot.court?.name || 'Корт'} (${slot.court?.club?.name || 'Клуб'})`}
                      >
                        {slot.date ? formatDate(slot.date.toString()) : ''} {slot.startTime || ''}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Таблица предложений */}
      <div className="bg-white rounded-lg shadow-md overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {!selectedLevel && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Уровень</th>
              )}
              {!selectedGroup && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Группа</th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Игроки</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Слот</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Статус</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ответы</th>
              <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Действия</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {proposals.map((proposal) => {
              const playerIds = [
                proposal.player1Id,
                proposal.player2Id,
                proposal.player3Id,
                proposal.player4Id
              ].filter(Boolean);
              
              return (
                <tr key={proposal.id}>
                  {!selectedLevel && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {proposal.group?.level || '—'}
                    </td>
                  )}
                  {!selectedGroup && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm">
                      {proposal.group?.name || 'Без группы'}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <div>
                      {proposal.player1?.name || 'Игрок 1'} vs {proposal.player2?.name || 'Игрок 2'}
                      {proposal.player3 && (
                        <div className="text-gray-500 text-xs">
                          {proposal.player3?.name || 'Игрок 3'} vs {proposal.player4?.name || 'Игрок 4'}
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <div>
                      {proposal.availability?.date && formatDate(proposal.availability.date)} {proposal.availability?.startTime && formatTime(proposal.availability.startTime)}
                      {proposal.availability?.court && (
                        <div className="text-gray-500 text-xs">
                          {proposal.availability.court?.name || 'Корт'} ({proposal.availability.court?.club?.name || 'Клуб'})
                        </div>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(proposal.status)}`}>
                      {getStatusLabel(proposal.status)}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="space-y-1">
                      {playerIds.map((playerId) => {
                        const response = getPlayerResponse(proposal, playerId!);
                        const player = proposal.player1Id === playerId ? proposal.player1 :
                                      proposal.player2Id === playerId ? proposal.player2 :
                                      proposal.player3Id === playerId ? proposal.player3 :
                                      proposal.player4;
                        return (
                          <div key={playerId} className="text-xs">
                            {player?.name}: {response ? (
                              <span className={response.status === 'ACCEPTED' ? 'text-green-600' : 'text-red-600'}>
                                {response.status === 'ACCEPTED' ? '✓ Принял' : '✗ Отклонил'}
                              </span>
                            ) : (
                              <span className="text-gray-400">Ожидает</span>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleOpenResponseModal(proposal)}
                        className="text-blue-600 hover:text-blue-800"
                        disabled={proposal.status === 'ACCEPTED' || proposal.status === 'REJECTED' || proposal.status === 'MATCH_CREATED'}
                      >
                        Ответ
                      </button>
                      <button
                        onClick={() => handleDeleteProposal(proposal.id)}
                        className="text-red-600 hover:text-red-800"
                      >
                        Удалить
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        {proposals.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            Нет предложений
          </div>
        )}
      </div>

      {/* Модальное окно создания предложения */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <h2 className="text-xl font-bold mb-4">
              Создать предложение
              {selectedLevel && !selectedGroup && (
                <span className="block text-sm font-normal text-blue-600 mt-1">
                  Для всех групп уровня {selectedLevel}
                </span>
              )}
              {!selectedLevel && !selectedGroup && selectedTournament && (
                <span className="block text-sm font-normal text-blue-600 mt-1">
                  Для всех уровней и групп турнира "{selectedTournament.name}"
                </span>
              )}
            </h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Игрок 1 *
                </label>
                <select
                  value={selectedPlayer1}
                  onChange={(e) => setSelectedPlayer1(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Выберите игрока</option>
                  {/* Если есть список подтвердивших слот - показываем их, иначе всех игроков */}
                  {(selectedSlot && slotConfirmedPlayers.length > 0
                    ? slotConfirmedPlayers.map((pa: any) => pa.player).filter((p: any) => p)
                    : players.filter(p => p)
                  ).map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Игрок 2 *
                </label>
                <select
                  value={selectedPlayer2}
                  onChange={(e) => setSelectedPlayer2(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Выберите игрока</option>
                  {/* Если есть список подтвердивших слот - показываем их, иначе всех игроков */}
                  {(selectedSlot && slotConfirmedPlayers.length > 0
                    ? slotConfirmedPlayers.map((pa: any) => pa.player).filter((p: any) => p && p.id !== selectedPlayer1)
                    : players.filter(p => p && p.id !== selectedPlayer1)
                  ).map((p: any) => (
                    <option key={p.id} value={p.id}>{p.name}</option>
                  ))}
                </select>
              </div>

              {selectedTournament?.format === 'DOUBLES' && (
                <>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Игрок 3
                    </label>
                    <select
                      value={selectedPlayer3}
                      onChange={(e) => setSelectedPlayer3(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="">Выберите игрока</option>
                      {players.filter(p => p && p.id !== selectedPlayer1 && p.id !== selectedPlayer2).map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Игрок 4
                    </label>
                    <select
                      value={selectedPlayer4}
                      onChange={(e) => setSelectedPlayer4(e.target.value)}
                      className="w-full border border-gray-300 rounded-lg px-3 py-2"
                    >
                      <option value="">Выберите игрока</option>
                      {players.filter(p => p && p.id !== selectedPlayer1 && p.id !== selectedPlayer2 && p.id !== selectedPlayer3).map(p => (
                        <option key={p.id} value={p.id}>{p.name}</option>
                      ))}
                    </select>
                  </div>
                </>
              )}

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Доступный слот *
                </label>
                <select
                  value={selectedAvailability}
                  onChange={(e) => setSelectedAvailability(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Выберите слот</option>
                  {availableSlots.filter(slot => slot && slot.court && slot.court.club).map(slot => (
                    <option key={slot.id} value={slot.id}>
                      {formatDate(slot.date)} {formatTime(slot.startTime)} - {slot.court.name} ({slot.court.club.name})
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowModal(false);
                  resetModal();
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleCreateProposal}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
              >
                Создать
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Модальное окно ввода ответа */}
      {showResponseModal && responseProposal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4">Ввод ответа игрока</h2>
            
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Игрок
                </label>
                <select
                  value={responsePlayerId}
                  onChange={(e) => setResponsePlayerId(e.target.value)}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="">Выберите игрока</option>
                  {[
                    responseProposal.player1,
                    responseProposal.player2,
                    responseProposal.player3,
                    responseProposal.player4
                  ].filter(Boolean).map(p => {
                    const response = getPlayerResponse(responseProposal, p!.id);
                    return (
                      <option key={p!.id} value={p!.id}>
                        {p!.name} {response ? `(${response.status === 'ACCEPTED' ? 'Принял' : 'Отклонил'})` : ''}
                      </option>
                    );
                  })}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Ответ
                </label>
                <select
                  value={responseStatus}
                  onChange={(e) => setResponseStatus(e.target.value as 'ACCEPTED' | 'REJECTED')}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2"
                >
                  <option value="ACCEPTED">Принял</option>
                  <option value="REJECTED">Отклонил</option>
                </select>
              </div>
            </div>

            <div className="flex justify-end gap-2 mt-6">
              <button
                onClick={() => {
                  setShowResponseModal(false);
                  setResponseProposal(null);
                }}
                className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
              >
                Отмена
              </button>
              <button
                onClick={handleAddResponse}
                disabled={!responsePlayerId}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-300"
              >
                Сохранить
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MatchProposals;

